import React from 'react';
import { StyleSheet, View, Text, TouchableOpacity, useWindowDimensions } from 'react-native';

interface AIFeedbackCardProps {
    score: number;
    maxScore?: number;
    feedback: string;
    suggestions: string[];
    label?: string;
    tipsTitle?: string;
    buttonText?: string;
    onDismiss: () => void;
}

export const AIFeedbackCard: React.FC<AIFeedbackCardProps> = ({ score, maxScore: _maxScore, feedback, suggestions = [], label, tipsTitle, buttonText, onDismiss }) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    return (
        <View style={styles.overlay}>
            <View style={[styles.card, isLandscape && styles.landscapeCard]} accessibilityRole="summary" accessibilityLabel={`AI feedback: score ${score} out of ${_maxScore || 10}`}>
                <View style={[styles.scoreCircle, isLandscape && styles.landscapeScoreCircle]} accessibilityRole="text" accessibilityLabel={`Score: ${score}`}>
                    <Text style={[styles.scoreText, isLandscape && styles.landscapeScoreText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{score}</Text>
                    <Text style={styles.scoreLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{label || 'Neatness'}</Text>
                </View>
                {!isLandscape && <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{feedback}</Text>}
                <View style={[styles.suggestionsContainer, isLandscape && styles.landscapeSuggestionsContainer]}>
                    <Text style={styles.suggestionsTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{tipsTitle || 'Coaching Tips:'}</Text>
                    {suggestions.map((tip, i) => (
                        <Text key={i} style={[styles.tipText, isLandscape && styles.landscapeTipText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            • <Text allowFontScaling={true} maxFontSizeMultiplier={1.5}>{tip}</Text>
                        </Text>
                    ))}
                </View>
                <TouchableOpacity style={[styles.button, isLandscape && styles.landscapeButton]} onPress={onDismiss} accessibilityRole="button" accessibilityLabel={buttonText || 'Next'} accessibilityHint="Double tap to continue">
                    <Text style={[styles.buttonText, isLandscape && styles.landscapeButtonText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{buttonText || 'NEXT! ➔'}</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
    },
    card: {
        width: '85%',
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 30,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 20,
        elevation: 10,
    },
    scoreCircle: {
        width: 128,
        height: 128,
        borderRadius: 64,
        borderWidth: 6,
        borderColor: '#22c55e',
        backgroundColor: '#F0FDF4',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 8,
        marginBottom: 18,
    },
    scoreText: {
        fontSize: 34,
        fontWeight: 'bold',
        color: '#15803D',
        textAlign: 'center',
    },
    scoreLabel: {
        fontSize: 11,
        color: '#166534',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        letterSpacing: 0.5,
        marginTop: 2,
        textAlign: 'center',
    },
    feedbackText: {
        fontSize: 18,
        color: '#334155',
        textAlign: 'center',
        lineHeight: 26,
        marginBottom: 24,
    },
    suggestionsContainer: {
        width: '100%',
        backgroundColor: '#F8FAFC',
        borderRadius: 16,
        padding: 20,
        marginBottom: 30,
    },
    suggestionsTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#475569',
        marginBottom: 10,
    },
    tipText: {
        fontSize: 14,
        color: '#64748B',
        marginBottom: 6,
    },
    button: {
        width: '100%',
        height: 60,
        backgroundColor: '#22c55e',
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '900',
    },
    landscapeCard: { width: '60%', padding: 15, borderRadius: 16 },
    landscapeScoreCircle: { width: 84, height: 84, borderRadius: 42, borderWidth: 4, marginBottom: 8, padding: 4 },
    landscapeScoreText: { fontSize: 24 },
    landscapeSuggestionsContainer: { padding: 10, marginBottom: 10 },
    landscapeTipText: { fontSize: 12, marginBottom: 2 },
    landscapeButton: { height: 56, minHeight: 56, borderRadius: 12 },
    landscapeButtonText: { fontSize: 16 },
});
